import { t } from '@lingui/core/macro'
import { getFirstFromSingleElementArray } from '@tecsinapse/es-utils/build'
import { USUARIO_BY_CPF_QUERY } from '../data/usuario-by-cpf-query'
import { USUARIO_BY_EMAIL_QUERY } from '../data/usuario-by-email-query'
import yup from '@/core/utils/yup-validation'
import { FieldValues, SubmitHandler } from 'react-hook-form'
import { OptionSelect } from '@/core/utils/types'
import { getLabel } from '@/core/utils/global-labels'
import { TipoDocumento } from '../../../core/enums/tipo-documento'
import { QueryClient } from '@tanstack/react-query'
import { request as globalRequest } from '../../../core/utils/request'
import {
  UsuarioByCpfQueryQuery,
  UsuarioByCpfQueryQueryVariables,
  UsuarioByEmailQueryQuery,
  UsuarioByEmailQueryQueryVariables
} from '../../../core/types/graphql'
import { getGraphQLErrorMessage } from '@/core/utils/graphql-error'

export const createSchemaArray = (
  produtos,
  client: QueryClient,
  request = globalRequest,
  tipoAcessoMap,
  tiposUsuarioDocumentoObrigatorio,
  tiposUsuarioInformarCargo,
  tiposUsuarioInformarGestao
) => {
  let emailDebounceTimer: ReturnType<typeof setTimeout> | null = null
  let emailCache: { value: string; result: boolean } | null = null

  const schemaArray = [
    yup.object({
      nome: yup
        .string()
        .required()
        .label(t`Nome`),
      email: yup
        .string()
        .email()
        .required()
        .label(t`E-mail`)
        .test(
          'email-validation',
          t`O e-mail já está sendo utilizado`,
          function emailAlreadyExistsValidation(value) {
            if (!value || this.parent.id) {
              return true
            }

            if (emailCache && emailCache.value === value) {
              return emailCache.result
            }

            return new Promise(resolve => {
              if (emailDebounceTimer) clearTimeout(emailDebounceTimer)
              emailDebounceTimer = setTimeout(async () => {
                const { usuarioByEmail } = await client.fetchQuery({
                  queryKey: ['usuarioByEmail', value],
                  queryFn: async () =>
                    await request<
                      UsuarioByEmailQueryQuery,
                      UsuarioByEmailQueryQueryVariables
                    >(USUARIO_BY_EMAIL_QUERY, {
                      variables: { email: value }
                    })
                })
                const result = !usuarioByEmail
                emailCache = { value, result }
                resolve(result)
              }, 500)
            })
          }
        ),
      cpf: yup
        .string()
        .nullable()
        .cpf(t`O CPF é inválido`)
        .test(
          'cpf-required',
          t`O CPF deve ser informado`,
          async function cpfRequiredValidation(value) {
            if (!this.parent.tipo || this.parent.isEstrangeiro) {
              return true
            }

            return tiposUsuarioDocumentoObrigatorio.some(
              tipo => tipo.id === this.parent.tipo.value
            )
              ? !!value
              : true
          }
        )
        .test(
          'cpf-validation',
          t`O CPF já está sendo utilizado`,
          async function cpfAlreadyExistsValidation(value) {
            if (!value || value.length !== 11) {
              return true
            }

            const { usuarioByCpf } = await client.fetchQuery({
              queryKey: ['usuarioByCpf', value],
              queryFn: async () =>
                await request<
                  UsuarioByCpfQueryQuery,
                  UsuarioByCpfQueryQueryVariables
                >(USUARIO_BY_CPF_QUERY, {
                  variables: { cpf: value }
                })
            })

            return !usuarioByCpf || usuarioByCpf.id === this.parent.id
          }
        ),
      codigos: yup.array().of(yup.string().required()),
      tipo: yup
        .object()
        .test(
          'has-required-fields',
          t`Tipo Usuário é um campo obrigatório`,
          value => value && !!value.label && !!value.value
        ),
      idsDealers: yup
        .array()
        .of(yup.object().shape({ label: yup.string(), value: yup.string() }))
        .label(getLabel('label.dealers'))
        .test(
          'DealersValidation',
          t`Informe ao menos um item`,
          function (value) {
            const tipo = this.parent?.tipo
            if (tipo?.value === 'DEALER_GRUPO') {
              return (
                Array.isArray(value) &&
                value?.length > 0 &&
                value?.every(dealer => dealer.value)
              )
            }
            return true
          }
        ),
      dataAdmissao: yup
        .date()
        .test(
          'data-admissao-test',
          t`Data de admissão é um campo obrigatório`,
          function (value) {
            if (
              tiposUsuarioInformarGestao.some(
                tu => tu.id === this.parent.tipo.value
              )
            ) {
              return value
            }
            return true
          }
        ),
      emailSuperior: yup
        .string()
        .email(t`Endereço de email inválido`)
        .test(
          'email-superior-test',
          t`Email do superior é um campo obrigatório`,
          function (value) {
            if (
              tiposUsuarioInformarGestao.some(
                tu => tu.id === this.parent.tipo.value
              )
            ) {
              return value && value.length > 0
            }
            return true
          }
        ),
      cargos: yup
        .array()
        .of(
          yup.object({
            value: yup.string(),
            label: yup.string(),
            dataInicio: yup.date(),
            dealerId: yup.string(),
            dealerNome: yup.string(),
            departamentoId: yup.string(),
            departamentoNome: yup.string()
          })
        )
        .test(
          'cargos-test',
          t`Preencha os cargos do usuário e clique no ícone (+) para adicioná-los`,
          function (cargoValues) {
            const { idsDealers, tipo } = this.parent
            if (tiposUsuarioInformarCargo.some(tu => tu.id === tipo.value)) {
              if (!Array.isArray(cargoValues) || cargoValues.length === 0) {
                return false
              }

              const missingCargoForSomeDealer = idsDealers.some(
                dealer =>
                  !cargoValues.some(cargo => cargo.dealerId === dealer.value)
              )

              if (missingCargoForSomeDealer) {
                return false
              }
            }
            return true
          }
        )
    }),
    yup.object({
      produtos: yup.object(
        produtos?.reduce(
          (acc, produto) => ({
            ...acc,
            [produto.id]: yup.object({
              funcionalidades: yup.array().of(yup.string()),
              idsTiposAcesso: yup
                .array()
                .of(yup.object())
                .test(
                  'verifica se pelo menos 1 das funcionalidades estão habilitadas para os tipos de acesso',
                  null,
                  function (value: OptionSelect[]) {
                    const idsTiposAcesso = (value ?? []).map(v => v?.value)
                    const { funcionalidades } = this.parent
                    const errorPath = []

                    idsTiposAcesso.forEach(idTipoAcesso => {
                      const tipoAcesso = tipoAcessoMap[idTipoAcesso]

                      const possuiFuncionalidadeSelecionada =
                        funcionalidades && funcionalidades.length > 0

                      if (!possuiFuncionalidadeSelecionada && tipoAcesso?.id) {
                        errorPath.push(tipoAcesso?.nome)
                      }
                    })
                    if (errorPath?.length) {
                      const message =
                        t`Produto ${produto.nome}: Habilite ao menos uma permissão para os acessos:`
                          .concat('\n')
                          .concat(errorPath.join('\n'))
                      return this.createError({
                        message
                      })
                    }
                    return true
                  }
                )
            })
          }),
          {}
        )
      )
    })
  ]

  return schemaArray
}

export const createInitialValues = (
  usuarioQuery,
  produtos,
  marcas,
  tiposUsuario,
  cargos,
  infoUsuario
) => {
  const {
    usuario = {
      id: null,
      nome: '',
      email: '',
      cpf: '',
      tipoDocumento: '',
      tipo: {},
      marcas: [],
      telefones: [],
      celulares: [],
      funcionalidades: [],
      dealers: [],
      grupos: [],
      tiposAcesso: []
    }
  } = usuarioQuery || {}
  const tipoUsuarioUnico = getFirstFromSingleElementArray(tiposUsuario ?? [])
  const { telefones, celulares } = usuario
  const tipo = tipoUsuarioUnico
    ? { value: tipoUsuarioUnico.id, label: tipoUsuarioUnico.description }
    : { value: usuario.tipo.id, label: usuario.tipo.description }
  const dealers = usuario.dealers.map(dealer => ({
    value: dealer.id,
    label: dealer.nomeExibicao,
    active: dealer.active
  }))

  const initialValues = {
    id: usuario.id,
    nome: usuario.nome,
    email: usuario.email,
    cpf: usuario.cpf,
    tipo,
    marcas:
      usuario.marcas.length > 0
        ? usuario.marcas?.map(marca => ({
            value: marca.id,
            label: marca.description
          }))
        : marcas?.map(marca => ({
            value: marca.id,
            label: marca.description
          })),
    telefones: telefones.length > 0 ? telefones : [],
    celulares: celulares.length > 0 ? celulares : [],
    idsGrupos: usuario.grupos.map(grupo => ({
      value: grupo.id,
      label: grupo.nome
    })),
    idsDealers: dealers,
    produtos: usuario.funcionalidades.reduce((acc, funcionalidade) => {
      const idProduto = funcionalidade.produto.id
      if (acc[idProduto]) {
        acc[idProduto].funcionalidades.push(funcionalidade.id)
      } else {
        acc[idProduto] = {
          funcionalidades: [funcionalidade.id],
          idsTiposAcesso: []
        }
      }
      return acc
    }, {}),
    cargos: cargos?.map(cargo => ({
      value: cargo.id,
      label: cargo.nome,
      dataInicio: cargo.dataInicio,
      dealerId: cargo?.dealer?.id ?? '',
      dealerNome: cargo?.dealer?.nome ?? '',
      departamentoId: cargo?.departamento?.id ?? '',
      departamentoNome: cargo?.departamento?.nome ?? ''
    })),
    dataAdmissao: infoUsuario?.dataAdmissao,
    emailSuperior: infoUsuario?.emailSuperior ?? '',
    isEstrangeiro: usuario.isEstrangeiro
  }
  usuario.tiposAcesso.forEach(tipoAcesso => {
    const idProduto = tipoAcesso.produto.id
    if (initialValues.produtos[idProduto]) {
      initialValues.produtos[idProduto].idsTiposAcesso.push({
        label: tipoAcesso.nome,
        value: tipoAcesso.id
      })
    }
  })
  return initialValues
}

export const createSubmitHandler = (
  salvarUsuario,
  tiposUsuarioInformarGestao,
  onClose
): SubmitHandler<FieldValues> => {
  return async _usuario => {
    try {
      const {
        idsGrupos,
        cargos = [],
        dataAdmissao,
        emailSuperior,
        isEstrangeiro,
        ...usuario
      } = _usuario

      const infos = tiposUsuarioInformarGestao.some(
        tu => tu.id === usuario.tipo.value
      )
        ? {
            dataAdmissao,
            emailSuperior
          }
        : null

      const usuarioInput = {
        ...usuario,
        idsDealers: usuario.idsDealers.map(dealer => dealer.value),
        marcas: usuario.marcas.map(marca => marca.value),
        tipo: usuario.tipo.value,
        cpf: usuario.cpf?.trim() ? usuario.cpf?.trim() : null,
        produtos: Object.keys(usuario.produtos).map(key => {
          const produto = usuario.produtos[key]
          return {
            idProduto: key,
            idsFuncionalidades: produto.funcionalidades ?? [],
            idsTiposAcesso: (produto.idsTiposAcesso ?? []).map(
              tipoAcesso => tipoAcesso.value
            )
          }
        }),
        tipoDocumento: isEstrangeiro
          ? TipoDocumento.ESTRANGEIRO
          : TipoDocumento.CPF
      }

      const { nome } = usuario
      const acao = usuario.id ? t`editado` : t`criado`
      const successMessage = t`Usuário ${nome} ${acao} com sucesso!`

      const cargosInput = cargos.map(it => {
        const dealerId =
          it.dealerId?.length > 0 ? { dealerId: it.dealerId } : {}
        const departamentoId =
          it.departamentoId?.length > 0
            ? { departamentoId: it.departamentoId }
            : {}
        return {
          dataInicio: it.dataInicio,
          cargoId: it.value,
          ...departamentoId,
          ...dealerId
        }
      })

      await salvarUsuario(
        usuarioInput,
        cargosInput,
        infos,
        successMessage,
        onClose
      )
    } catch (error) {
      throw new Error(getGraphQLErrorMessage(error))
    }
  }
}
